import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { BookOpen, GraduationCap, Loader2, Clock, Home, PlayCircle, CheckCircle2, AlertCircle, MessageCircle, Star, Sparkles, Search, X } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import CourseView from "@/pages/CourseView";
import StreaksAndBadges from "@/components/StreaksAndBadges";
import Leaderboard from "@/components/Leaderboard";
import ReferralCard from "@/components/ReferralCard";
import PushNotificationCard from "@/components/PushNotificationCard";
import TelegramLinkCard from "@/components/TelegramLinkCard";
import TelegramSubscribeModal from "@/components/TelegramSubscribeModal";
import OnboardingChecklist from "@/components/OnboardingChecklist";
import CommandPalette from "@/components/CommandPalette";
import { useStreakReminder } from "@/hooks/useStreakReminder";
import { Skeleton } from "@/components/ui/skeleton";
import SEO from "@/components/SEO";

const translations = {
  en: {
    welcome: "Welcome",
    dashboard: "Your learning dashboard",
    home: "Home",
    noCourses: "No courses available yet",
    checkBack: "Check back soon!",
    enrolled: "Enrolled",
    pending: "Pending",
    rejected: "Rejected",
    continueLearning: "Continue Learning",
    awaitingApproval: "Your enrollment request is being reviewed by our team.",
    enrollmentRejected: "Enrollment was rejected. Contact support.",
    requestEnrollment: "Request Enrollment",
    lessons: "lessons",
    alreadyRequested: "Already requested",
    status: "Status",
    enrollmentRequested: "Enrollment requested!",
    waitingApproval: "Waiting for admin approval.",
    error: "Error",
    completed: "completed",
    notStarted: "Not started yet",
    inProgress: "In progress",
    courseCompleted: "Course completed",
    freeCourses: "Free Classes",
    paidCourses: "Premium Class",
    premiumClassName: "Premium Class",
    premiumDescription: "Master advanced trading strategies including (SK), Price Action analysis, Volume trading, Trading Psychology and Risk Management",
    contactSupport: "Contact Fitawrari Support",
  },
  am: {
    welcome: "እንኳን ደህና መጡ",
    dashboard: "የመማር ዳሽቦርድዎ",
    home: "መነሻ",
    noCourses: "እስካሁን ምንም ኮርስ የለም",
    checkBack: "በቅርቡ ይመልከቱ!",
    enrolled: "ተመዝግቧል",
    pending: "በመጠባበቅ ላይ",
    rejected: "ተቀባይነት አላገኘም",
    continueLearning: "መማር ይቀጥሉ",
    awaitingApproval: "የምዝገባ ጥያቄዎ በቡድናችን እየተገመገመ ነው።",
    enrollmentRejected: "ምዝገባ ተቀባይነት አላገኘም። ድጋፍ ያግኙ።",
    requestEnrollment: "ምዝገባ ይጠይቁ",
    lessons: "ትምህርቶች",
    alreadyRequested: "ቀድሞ ተጠይቋል",
    status: "ሁኔታ",
    enrollmentRequested: "ምዝገባ ተጠይቋል!",
    waitingApproval: "የአስተዳዳሪ ማጽደቅ በመጠባበቅ ላይ።",
    error: "ስህተት",
    completed: "ተጠናቋል",
    notStarted: "ገና አልተጀመረም",
    inProgress: "በሂደት ላይ",
    courseCompleted: "ኮርስ ተጠናቋል",
    freeCourses: "ነፃ ትምህርቶች",
    paidCourses: "ልዩ ትምህርት (Premium Class)",
    premiumClassName: "Premium Class",
    premiumDescription: "የላቀ የንግድ ስልቶችን ማስተር ያድርጉ - (SK)፣ Price Action ትንተና፣ Volume trading፣ Trading Psychology እና Risk Management ይጨምራል።",
    contactSupport: "የFitawrari FX ድጋፍን ያግኙ (Contact Support)",
  },
};

type Course = { id: string; title: string; description: string | null; thumbnail_url: string | null; is_paid?: boolean; support_link?: string | null };
type Enrollment = { course_id: string; status: string };
type PremiumGroup = { id: string; name: string; description: string | null; start_date: string | null; is_active: boolean | null; price_cents?: number; currency?: string; max_seats?: number | null; enrollment_deadline?: string | null; enrolled_count?: number };

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { toast } = useToast();
  const [courses, setCourses] = useState<Course[]>([]);
  const [enrollments, setEnrollments] = useState<Enrollment[]>([]);
  const [premiumGroups, setPremiumGroups] = useState<PremiumGroup[]>([]);
  const [allLessons, setAllLessons] = useState<{ id: string; title: string; course_id: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [requesting, setRequesting] = useState<string | null>(null);
  const [progressMap, setProgressMap] = useState<Record<string, { completed: number; total: number }>>({});
  const [selectedCourseId, setSelectedCourseId] = useState<string | null>(null);
  const [hasTelegram, setHasTelegram] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [lang, setLang] = useState<"en" | "am">(() => {
    return (localStorage.getItem("fitawrari_lang") as "en" | "am") || "en";
  });

  // Always-on streak reminder (auto-prompts permission once)
  useStreakReminder(user?.id);

  // Open course from URL (?course=...) — used by notification deep-links
  useEffect(() => {
    const c = searchParams.get("course");
    if (c && c !== selectedCourseId) setSelectedCourseId(c);
  }, [searchParams]);

  const toggleLang = () => {
    const newLang = lang === "en" ? "am" : "en";
    setLang(newLang);
    localStorage.setItem("fitawrari_lang", newLang);
  };

  const t = translations[lang];
  const fullName = user?.user_metadata?.full_name || "Student";

  useEffect(() => {
    const fetchData = async () => {
      if (!user?.id) return;
      const [coursesRes, enrollmentsRes] = await Promise.all([
        supabase.from("courses").select("id, title, description, thumbnail_url, is_paid, support_link").eq("is_published", true).order("sort_order"),
        supabase.from("student_enrollments").select("course_id, status").eq("user_id", user.id),
      ]);
      setCourses((coursesRes.data as Course[]) || []);
      setEnrollments((enrollmentsRes.data as Enrollment[]) || []);

      const approved = (enrollmentsRes.data || []).filter((e: Enrollment) => e.status === "approved");
      if (approved.length > 0) {
        const courseIds = approved.map((e: Enrollment) => e.course_id);
        const lessonsRes = await supabase.from("lessons").select("id, title, course_id").in("course_id", courseIds);
        const progressRes = await supabase.from("student_lesson_progress").select("lesson_id, exam_passed").eq("user_id", user.id);
        const lessons = lessonsRes.data || [];
        setAllLessons(lessons as { id: string; title: string; course_id: string }[]);
        const progress = progressRes.data || [];
        const passedSet = new Set(progress.filter((p) => p.exam_passed).map((p) => p.lesson_id));
        const map: Record<string, { completed: number; total: number }> = {};
        for (const cid of courseIds) {
          const cl = lessons.filter((l) => l.course_id === cid);
          map[cid] = { total: cl.length, completed: cl.filter((l) => passedSet.has(l.id)).length };
        }
        setProgressMap(map);
      }
      // Fetch Premium Cohorts
      if (user?.email) {
        const { data: premRes } = await (supabase as any)
          .from("premium_enrollments")
          .select("*, premium_groups(*)")
          .eq("email", user.email);
        if (premRes) setPremiumGroups(premRes.map((p: any) => p.premium_groups).filter(Boolean) as PremiumGroup[]);
      }

      if (user?.id) {
        const { data: tg } = await supabase.from("user_telegram").select("id").eq("user_id", user.id).maybeSingle();
        setHasTelegram(!!tg);
      }

      setLoading(false);
    };
    fetchData();
  }, [user?.email, user?.id]);

  const handleRequestEnrollment = async (courseId: string) => {
    setRequesting(courseId);
    try {
      const res = await supabase.functions.invoke("manage-certificates", {
        body: { action: "request_enrollment", courseId },
      });
      if (res.error || !res.data?.success) throw new Error(res.data?.error || "Failed");
      if (res.data.alreadyRequested) {
        toast({ title: t.alreadyRequested, description: `${t.status}: ${res.data.status}` });
      } else {
        toast({ title: t.enrollmentRequested, description: t.waitingApproval });
      }
      setEnrollments((prev) => [...prev, { course_id: courseId, status: "pending" }]);
    } catch (err) {
      const error = err as Error;
      toast({ title: t.error, description: err.message, variant: "destructive" });
    } finally { setRequesting(null); }
  };

  const enrollmentMap = new Map(enrollments.map((e) => [e.course_id, e.status]));

  if (selectedCourseId) {
    const lessonParam = searchParams.get("lesson") ?? undefined;
    return <CourseView courseId={selectedCourseId} initialLessonId={lessonParam} onBack={() => { setSelectedCourseId(null); setSearchParams({}); }} />;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="container mx-auto px-4 py-8 max-w-5xl pt-24 space-y-6">
          <div className="space-y-2">
            <Skeleton className="h-8 w-64" />
            <Skeleton className="h-4 w-48" />
          </div>
          <Skeleton className="h-28 w-full" />
          <Skeleton className="h-11 w-full" />
          <Skeleton className="h-12 w-full" />
          <div className="grid gap-6 md:grid-cols-2">
            {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-56 w-full" />)}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Student Dashboard — Fitawrari FX"
        description="Your Fitawrari FX learning dashboard: courses, progress, streaks, and rewards."
        path="/dashboard"
        noindex
      />
      <Navbar />
      <div className="container mx-auto px-4 py-8 max-w-5xl pt-24">
        <div className="mb-8 flex items-start justify-between">
          <div>
            <h1 className="font-display text-3xl font-bold mb-1">{t.welcome}, {fullName}!</h1>
            <p className="text-muted-foreground">{t.dashboard}</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => navigate("/p2p")} title="P2P USDT ⇄ ETB">
              P2P
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate("/dashboard/bookmarks")} title="My Bookmarks">
              <BookOpen className="w-4 h-4 mr-1" /> Bookmarks
            </Button>
            <Button variant="outline" size="sm" onClick={toggleLang}>
              {lang === "en" ? "አማ" : "EN"}
            </Button>
          </div>
        </div>

        <StreaksAndBadges
          userId={user?.id}
          completedLessons={Object.values(progressMap).reduce((sum, p) => sum + p.completed, 0)}
          enrolledCourses={enrollments.filter((e) => e.status === "approved").length + premiumGroups.length}
        />

        {user?.id && (
          <OnboardingChecklist
            userId={user.id}
            hasTelegramLinked={hasTelegram}
            hasWatchedLesson={Object.values(progressMap).some((p) => p.completed > 0)}
            hasEnrolled={enrollments.filter((e) => e.status === "approved").length + premiumGroups.length > 0}
          />
        )}

        <CommandPalette />

        {/* Global search */}
        <div className="mb-6 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
          <Input
            placeholder="Search courses or lessons..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 pr-9 h-11"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              aria-label="Clear search"
            >
              <X className="w-4 h-4" />
            </button>
          )}
          {searchQuery && (
            <SearchResults
              query={searchQuery}
              courses={courses}
              lessons={allLessons}
              onSelectCourse={(id) => { setSelectedCourseId(id); setSearchQuery(""); }}
              onClose={() => setSearchQuery("")}
            />
          )}
        </div>

        <div className="mb-8">
          <Tabs defaultValue="free" className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-8 h-12 p-1 bg-muted/50">
              <TabsTrigger value="free" className="text-sm font-semibold data-[state=active]:bg-background data-[state=active]:shadow-sm py-2">
                <Sparkles className="w-4 h-4 mr-2 text-primary" /> {t.freeCourses}
              </TabsTrigger>
              <TabsTrigger value="premium" className="text-sm font-semibold data-[state=active]:bg-background data-[state=active]:shadow-sm py-2">
                <Star className="w-4 h-4 mr-2 text-amber-500 fill-amber-500" /> {t.paidCourses}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="free" className="mt-0">
              {courses.filter(c => !c.is_paid).length === 0 ? (
                <EmptyCourses t={t} />
              ) : (
                <div className="grid gap-6 md:grid-cols-2">
                  {courses.filter(c => !c.is_paid).map((course) => (
                    <CourseCard 
                      key={course.id} 
                      course={course} 
                      status={enrollmentMap.get(course.id)} 
                      prog={progressMap[course.id]} 
                      t={t} 
                      onSelect={() => setSelectedCourseId(course.id)}
                      onRequest={() => handleRequestEnrollment(course.id)}
                      requesting={requesting === course.id}
                    />
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="premium" className="mt-0">
              <div className="space-y-8">
                {premiumGroups.length > 0 && (
                  <div className="space-y-4">
                    <h3 className="text-lg font-bold flex items-center gap-2 text-amber-600">
                      <Sparkles className="w-5 h-5" /> Active Premium Cohorts
                    </h3>
                    <div className="grid gap-6 md:grid-cols-2">
                      {premiumGroups.map((group) => (
                        <Card 
                          key={group.id} 
                          className="overflow-hidden border-amber-500/20 bg-gradient-to-br from-amber-500/5 to-transparent cursor-pointer hover:shadow-md transition-all"
                          onClick={() => navigate(`/dashboard/premium/${group.id}`)}
                        >
                          <CardContent className="p-6">
                            <div className="flex justify-between items-start mb-4">
                              <div>
                                <h4 className="text-xl font-bold">{group.name}</h4>
                                <p className="text-sm text-muted-foreground mt-1">{group.description}</p>
                              </div>
                              <Badge className="bg-amber-500 text-white">LIVE COHORT</Badge>
                            </div>
                            <div className="grid grid-cols-2 gap-3 text-sm mb-4">
                              <div className="flex items-center gap-1 text-muted-foreground">
                                <Clock className="w-4 h-4" /> {group.start_date ? new Date(group.start_date).toLocaleDateString() : "Pending"}
                              </div>
                              {group.price_cents ? (
                                <div className="text-right font-bold text-amber-700">
                                  {(group.price_cents / 100).toFixed(2)} {group.currency || "USD"}
                                </div>
                              ) : null}
                              {group.max_seats ? (
                                <div className="text-muted-foreground">
                                  Seats: {Math.max(0, group.max_seats - (group.enrolled_count || 0))} left
                                </div>
                              ) : null}
                              {group.enrollment_deadline ? (
                                <div className="text-right text-muted-foreground">
                                  Closes {new Date(group.enrollment_deadline).toLocaleDateString()}
                                </div>
                              ) : null}
                            </div>
                            <Button size="sm" variant="outline" className="w-full border-amber-500 text-amber-700 hover:bg-amber-500 hover:text-white">
                              View Content
                            </Button>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}

                <div className="space-y-4">
                  <h3 className="text-lg font-bold flex items-center gap-2 text-primary">
                    <BookOpen className="w-5 h-5" /> {t.paidCourses}
                  </h3>
                  {courses.filter(c => c.is_paid).length === 0 ? (
                    <EmptyCourses t={t} />
                  ) : (
                    <div className="grid gap-6 md:grid-cols-2">
                      {courses.filter(c => c.is_paid).map((course) => (
                        <CourseCard 
                          key={course.id} 
                          course={course} 
                          status={enrollmentMap.get(course.id)} 
                          prog={progressMap[course.id]} 
                          t={t} 
                          onSelect={() => setSelectedCourseId(course.id)}
                          onRequest={() => handleRequestEnrollment(course.id)}
                          requesting={requesting === course.id}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>

        {/* Engagement & sharing — moved below courses */}
        <div className="space-y-0">
          {user?.id && <ReferralCard userId={user.id} />}
          {user?.id && <PushNotificationCard userId={user.id} />}
          {/* Streak reminders are now always-on (see useStreakReminder hook) */}
          {user?.id && <TelegramLinkCard userId={user.id} />}
          {user?.id && <TelegramSubscribeModal userId={user.id} />}
          <Leaderboard />
        </div>
      </div>
    </div>
  );
};

const EmptyCourses = ({ t }: { t: Record<string, string> }) => {
  const navigate = useNavigate();
  return (
    <Card className="overflow-hidden border-primary/20 bg-gradient-to-br from-primary/5 via-amber-500/5 to-transparent">
      <CardContent className="py-12 text-center space-y-5">
        <div className="w-16 h-16 mx-auto rounded-full bg-primary/10 flex items-center justify-center">
          <Sparkles className="w-8 h-8 text-primary" />
        </div>
        <div>
          <h3 className="text-xl font-bold mb-2">Ready to level up your trading?</h3>
          <p className="text-muted-foreground max-w-md mx-auto">
            Explore our mentorship plans and join the next cohort to unlock premium classes, live analysis and 1-on-1 mentoring.
          </p>
        </div>
        <Button size="lg" className="bg-amber-500 hover:bg-amber-600 text-white" onClick={() => navigate("/", { state: { scrollTo: "pricing" } })}>
          <Star className="w-4 h-4 mr-2 fill-white" />
          View Pricing & Plans
        </Button>
      </CardContent>
    </Card>
  );
};

interface CourseCardProps {
  course: Course;
  status?: string;
  prog?: { completed: number; total: number };
  t: Record<string, string>;
  onSelect: () => void;
  onRequest: () => void;
  requesting: boolean;
}

const CourseCard = ({ course, status, prog, t, onSelect, onRequest, requesting }: CourseCardProps) => {
  const progressPct = prog && prog.total > 0 ? Math.round((prog.completed / prog.total) * 100) : 0;
  const isComplete = prog && prog.total > 0 && prog.completed === prog.total;

  return (
    <Card className={`overflow-hidden flex flex-col transition-shadow hover:shadow-lg ${status === "approved" ? "ring-1 ring-primary/20" : ""}`}>
      {course.thumbnail_url ? (
        <div className="relative">
          <img src={course.thumbnail_url} alt={course.title} className="w-full h-48 object-cover" />
          {course.is_paid && (
            <div className="absolute top-3 left-3">
              <Badge className="bg-amber-500 text-white shadow-md font-bold tracking-wide">
                ★ {t.premiumClassName}
              </Badge>
            </div>
          )}
          {status === "approved" && (
            <div className="absolute top-3 right-3">
              <Badge className="bg-primary/90 text-primary-foreground shadow-sm">
                <CheckCircle2 className="w-3 h-3 mr-1" /> {t.enrolled}
              </Badge>
            </div>
          )}
          {status === "pending" && (
            <div className="absolute top-3 right-3">
              <Badge variant="secondary" className="shadow-sm">
                <Clock className="w-3 h-3 mr-1" /> {t.pending}
              </Badge>
            </div>
          )}
          {status === "rejected" && (
            <div className="absolute top-3 right-3">
              <Badge variant="destructive" className="shadow-sm">{t.rejected}</Badge>
            </div>
          )}
        </div>
      ) : course.is_paid ? (
        <div className="relative w-full h-48 bg-gradient-to-br from-amber-500/20 via-amber-600/10 to-primary/10 flex items-center justify-center">
          <div className="text-center">
            <span className="text-4xl">👑</span>
            <Badge className="mt-2 bg-amber-500 text-white shadow-md font-bold tracking-wide block mx-auto w-fit">
              ★ {t.premiumClassName}
            </Badge>
          </div>
        </div>
      ) : null}

      <div className="flex flex-col flex-1 p-5">
        <div className="mb-3">
          <h3 className="font-display text-xl font-bold mb-2 leading-tight">
            {course.is_paid ? t.premiumClassName : course.title}
          </h3>
          {course.is_paid ? (
            <p className="text-sm text-muted-foreground leading-relaxed line-clamp-3">{t.premiumDescription}</p>
          ) : course.description && (
            <p className="text-sm text-muted-foreground leading-relaxed line-clamp-3">{course.description}</p>
          )}
        </div>

        <div className="mt-auto space-y-4">
          {status === "approved" && (
            <>
              {prog && prog.total > 0 && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground flex items-center gap-1">
                      <BookOpen className="w-3.5 h-3.5" />
                      {prog.completed}/{prog.total} {t.lessons} {t.completed}
                    </span>
                    <span className={`font-semibold ${isComplete ? "text-green-600" : "text-primary"}`}>
                      {progressPct}%
                    </span>
                  </div>
                  <Progress value={progressPct} className="h-2.5" />
                  <p className="text-xs text-muted-foreground">
                    {isComplete ? `🎉 ${t.courseCompleted}` : progressPct === 0 ? t.notStarted : t.inProgress}
                  </p>
                </div>
              )}
              <Button className="w-full" size="lg" onClick={onSelect}>
                <PlayCircle className="w-4 h-4 mr-2" /> {t.continueLearning}
              </Button>
            </>
          )}

          {status === "pending" && (
            <div className="rounded-lg bg-muted/50 p-4 text-center">
              <Clock className="w-6 h-6 mx-auto mb-2 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">{t.awaitingApproval}</p>
            </div>
          )}

          {status === "rejected" && (
            <div className="rounded-lg bg-destructive/10 p-4 text-center">
              <AlertCircle className="w-6 h-6 mx-auto mb-2 text-destructive" />
              <p className="text-sm text-destructive">{t.enrollmentRejected}</p>
            </div>
          )}

          {!status && (
            course.is_paid ? (
              <Button
                className="w-full"
                size="lg"
                variant="outline"
                onClick={() => window.open(course.support_link || "https://t.me/fitawrari_support", "_blank")}
              >
                <MessageCircle className="w-4 h-4 mr-2" />
                {t.contactSupport}
              </Button>
            ) : (
              <Button
                className="w-full"
                size="lg"
                variant="outline"
                onClick={onRequest}
                disabled={requesting}
              >
                {requesting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <GraduationCap className="w-4 h-4 mr-2" />}
                {t.requestEnrollment}
              </Button>
            )
          )}
        </div>
      </div>
    </Card>
  );
};

interface SearchResultsProps {
  query: string;
  courses: Course[];
  lessons: { id: string; title: string; course_id: string }[];
  onSelectCourse: (id: string) => void;
  onClose: () => void;
}

const SearchResults = ({ query, courses, lessons, onSelectCourse }: SearchResultsProps) => {
  const q = query.toLowerCase().trim();
  if (!q) return null;

  const matchedCourses = courses.filter(
    (c) => c.title.toLowerCase().includes(q) || (c.description || "").toLowerCase().includes(q)
  );
  const matchedLessons = lessons.filter((l) => l.title.toLowerCase().includes(q)).slice(0, 8);
  const courseTitleMap = new Map(courses.map((c) => [c.id, c.title]));

  if (matchedCourses.length === 0 && matchedLessons.length === 0) {
    return (
      <div className="absolute z-30 mt-2 w-full bg-popover border border-border rounded-lg shadow-lg p-4 text-sm text-muted-foreground text-center">
        No matches found
      </div>
    );
  }

  return (
    <div className="absolute z-30 mt-2 w-full bg-popover border border-border rounded-lg shadow-lg max-h-80 overflow-y-auto">
      {matchedCourses.length > 0 && (
        <div className="p-2">
          <p className="px-2 py-1 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Courses</p>
          {matchedCourses.map((c) => (
            <button
              key={c.id}
              onClick={() => onSelectCourse(c.id)}
              className="w-full text-left px-3 py-2 rounded-md hover:bg-muted transition-colors flex items-center gap-2"
            >
              <BookOpen className="w-4 h-4 text-primary shrink-0" />
              <span className="truncate">{c.title}</span>
            </button>
          ))}
        </div>
      )}
      {matchedLessons.length > 0 && (
        <div className="p-2 border-t border-border">
          <p className="px-2 py-1 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Lessons</p>
          {matchedLessons.map((l) => (
            <button
              key={l.id}
              onClick={() => onSelectCourse(l.course_id)}
              className="w-full text-left px-3 py-2 rounded-md hover:bg-muted transition-colors flex items-start gap-2"
            >
              <PlayCircle className="w-4 h-4 text-primary shrink-0 mt-0.5" />
              <div className="min-w-0">
                <p className="truncate text-sm">{l.title}</p>
                <p className="text-xs text-muted-foreground truncate">{courseTitleMap.get(l.course_id)}</p>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default Dashboard;
